import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userProfileApi } from '../../../services/userManagementApi';
import { ChangePasswordRequest } from '../../../types/userManagement';
import './ChangePassword.css';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ChangePasswordRequest>({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleInputChange = (field: keyof ChangePasswordRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reset states when user types
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const validateForm = (): string | null => {
    if (!formData.currentPassword.trim()) {
      return 'Inserisci la password corrente';
    }
    
    if (!formData.newPassword.trim()) {
      return 'Inserisci la nuova password';
    }
    
    if (formData.newPassword.length < 4) {
      return 'La nuova password deve essere di almeno 4 caratteri';
    }
    
    if (formData.newPassword !== formData.confirmNewPassword) {
      return 'Le password non coincidono';
    }
    
    if (formData.currentPassword === formData.newPassword) {
      return 'La nuova password deve essere diversa da quella corrente';
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await userProfileApi.changePassword(formData);
      setSuccess(true);
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 4) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    if (strength <= 2) return { strength, label: 'Debole', color: '#dc3545' };
    if (strength <= 4) return { strength, label: 'Media', color: '#ffc107' };
    return { strength, label: 'Forte', color: '#198754' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="change-password">
      <div className="change-password-container">
        <div className="change-password-header">
          <h1>Cambia Password</h1>
          <p>Modifica la tua password per mantenere sicuro il tuo account</p>
        </div>

        <form onSubmit={handleSubmit} className="change-password-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              Password cambiata con successo!
            </div>
          )}

          {/* Password Corrente */}
          <div className="form-group">
            <label htmlFor="currentPassword">Password Corrente *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                value={formData.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                placeholder="Inserisci la tua password corrente"
                disabled={loading}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('current')}
                tabIndex={-1}
              >
                {showPasswords.current ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Nuova Password */}
          <div className="form-group">
            <label htmlFor="newPassword">Nuova Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                value={formData.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                placeholder="Inserisci la nuova password (min. 4 caratteri)"
                disabled={loading}
                required
                minLength={4}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('new')}
                tabIndex={-1}
              >
                {showPasswords.new ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formData.newPassword && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div 
                    className="strength-fill" 
                    style={{ 
                      width: `${(passwordStrength.strength / 6) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  />
                </div>
                <span className="strength-label" style={{ color: passwordStrength.color }}>
                  Sicurezza: {passwordStrength.label}
                </span>
              </div>
            )}
          </div>

          {/* Conferma Password */}
          <div className="form-group">
            <label htmlFor="confirmNewPassword">Conferma Nuova Password *</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmNewPassword"
                value={formData.confirmNewPassword}
                onChange={(e) => handleInputChange('confirmNewPassword', e.target.value)}
                placeholder="Conferma la nuova password"
                disabled={loading}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirm')}
                tabIndex={-1}
              >
                {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {/* Password Match Indicator */}
            {formData.confirmNewPassword && (
              <div className={`password-match ${formData.newPassword === formData.confirmNewPassword ? 'match' : 'no-match'}`}>
                {formData.newPassword === formData.confirmNewPassword ? (
                  <span className="match-text">✅ Le password coincidono</span>
                ) : (
                  <span className="no-match-text">❌ Le password non coincidono</span>
                )}
              </div>
            )}
          </div>

          {/* Security Tips */}
          <div className="security-tips">
            <h4>Consigli per una password sicura:</h4>
            <ul>
              <li>Usa almeno 8 caratteri</li>
              <li>Includi lettere maiuscole e minuscole</li>
              <li>Aggiungi numeri e caratteri speciali</li>
              <li>Non usare parole comuni o informazioni personali</li>
              <li>Non riutilizzare password di altri account</li>
            </ul>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmNewPassword}
            >
              {loading ? 'Cambiando Password...' : 'Cambia Password'}
            </button>
            
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Torna Indietro
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;